// AppHeader.js — top bar: menu button, logo mark + wordmark (links home), the
// main links from 1200px (with Play, the daily games, as a highlighted pill),
// and the icon buttons: Play below 1200px (the games are one tap away on
// phones), search, X, and Buy Me a Coffee from 600px.

import React, { Suspense, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { AppBar, Box, IconButton, Toolbar, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CoffeeIcon from "@mui/icons-material/Coffee";
import SearchIcon from "@mui/icons-material/Search";
import ExtensionRounded from "@mui/icons-material/ExtensionRounded";
import { MenuButton } from "../SidebarNavigation";
import { BRAND, LOGO_CELLS, accentTint } from "../theme";
import { X_URL } from "../site";

export const HEADER_H = { mobile: 60, desktop: 72 };

// The quick search's code loads the first time it's wanted (or hovered).
const loadSearch = () => import("./search/SearchDialog");
const SearchDialog = React.lazy(loadSearch);

// Shown as text links on desktop; everything (Watch, Articles, About and FAQ
// included) is also in the menu drawer and footer. Play (the daily games) is
// a pill, highlighted on any of the three games' pages.
const GAME_PAGES = ["daily", "season", "hl"];
const NAV_LINKS = [
  { to: "/", label: "Grid", page: "home" },
  { to: "/records", label: "Records", page: "records" },
  { to: "/scorelines", label: "Scorelines", page: "scorelines" },
  { to: "/clubs", label: "Clubs", page: "clubs" },
  { to: "/head-to-head", label: "Head to head", page: "h2h" },
  { to: "/all-time-table", label: "Table", page: "table" },
  { to: "/seasons", label: "Seasons", page: "seasons" },
  { to: "/race", label: "Race", page: "race" },
  { to: "/daily", label: "Play", pages: GAME_PAGES, play: true },
];

// The logo mark (theme.js): a 3×3 slice of the grid in the palette's ramp.
export { LOGO_CELLS };

function LogoMark({ size }) {
  const gap = size * 0.07;
  const cell = (size - gap * 2) / 3;
  return (
    <Box component="svg" viewBox={`0 0 ${size} ${size}`} sx={{ width: size, height: size, flexShrink: 0 }} aria-hidden>
      {LOGO_CELLS.map((row, r) =>
        row.map((fill, c) => (
          <rect
            key={`${r}-${c}`}
            x={c * (cell + gap)}
            y={r * (cell + gap)}
            width={cell}
            height={cell}
            rx={cell * 0.22}
            fill={fill}
          />
        ))
      )}
    </Box>
  );
}

// The logo mark beside the FOOTYGAMI wordmark. Sizes take MUI responsive values.
export function BrandLockup({ logoSize, wordSize }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.25 }, minWidth: 0 }}>
      <LogoMark size={logoSize} />
      <Typography component="div" sx={{ color: "#fff", fontSize: wordSize, fontWeight: 900, letterSpacing: "0.01em", lineHeight: 1, whiteSpace: "nowrap" }}>
        FOOTYGAMI
      </Typography>
    </Box>
  );
}

export const XIcon = ({ size }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" sx={{ width: size, height: size, fill: "currentColor" }}>
    <path d="M18.9 2H21l-6.5 7.4L22 22h-7.1l-4.6-6.2L4.1 22H2l7-8-6.7-12H9l4 6.1L18.9 2z" />
  </Box>
);

const linkButtonSx = {
  width: 40,
  height: 40,
  color: "#e6e8ee",
  border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(255,255,255,0.03)",
  transition: "all 160ms ease",
  "&:hover": { color: "#fff", borderColor: BRAND.accent, background: accentTint(0.08) },
};

export default function AppHeader({ onMenuClick, currentPage }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // The logo, nine links and three icons need about 1,200px; below that the
  // menu button (which lists every page) does the job, with Play as an icon.
  const showNav = useMediaQuery("(min-width:1200px)");
  // Icons give way as the screen narrows: the coffee button below 600px and X
  // below 380px (both are in the footer on every page); Play and search stay.
  const tiny = useMediaQuery("(max-width:379px)");
  const [searching, setSearching] = useState(false);

  // ⌘K / Ctrl+K anywhere, or "/" when not typing, opens the search.
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable;
      if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") || (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey)) {
        e.preventDefault();
        setSearching(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <AppBar
      position="relative"
      elevation={0}
      sx={{
        height: isMobile ? HEADER_H.mobile : HEADER_H.desktop,
        background: "rgba(15,15,19,0.72)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        px: { xs: 1, sm: 3 },
        zIndex: 10,
        // A faint hairline along the bottom edge.
        "&::after": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: -1,
          height: "1px",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)",
        },
      }}
    >
      <Toolbar disableGutters sx={{ height: "100%", minHeight: "0 !important", display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1.5 }, minWidth: 0 }}>
          <MenuButton onClick={onMenuClick} />
          <Box
            component={RouterLink}
            to="/"
            aria-label="Footygami home"
            sx={{ display: "flex", alignItems: "center", textDecoration: "none", minWidth: 0 }}
          >
            <BrandLockup logoSize={isMobile ? 30 : 38} wordSize={{ xs: 23, sm: 29 }} />
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {showNav && (
            <Box component="nav" aria-label="Main" sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 1.5 }}>
              {NAV_LINKS.map((l) => {
                const active = l.pages ? l.pages.includes(currentPage) : currentPage === l.page;
                return (
                  <Box
                    key={l.to}
                    component={RouterLink}
                    to={l.to}
                    aria-current={active ? "page" : undefined}
                    sx={{
                      px: { xs: 1.1, lg: 1.25 },
                      py: 0.75,
                      borderRadius: 999,
                      color: active ? BRAND.accent : l.play ? BRAND.accent : "#c9ccd6",
                      fontSize: 14,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      textDecoration: "none",
                      background: active ? BRAND.accentSoft : "transparent",
                      transition: "all 160ms ease",
                      "&:hover": { color: "#fff", background: "rgba(255,255,255,0.06)" },
                      ...(l.play && {
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        ml: 0.5,
                        border: `1px solid ${accentTint(0.45)}`,
                        background: active ? accentTint(0.2) : accentTint(0.08),
                        "&:hover": { color: "#fff", borderColor: BRAND.accent, background: accentTint(0.16) },
                      }),
                    }}
                  >
                    {l.play && <ExtensionRounded sx={{ fontSize: 16 }} />}
                    {l.label}
                  </Box>
                );
              })}
            </Box>
          )}
          {!showNav && (
            <IconButton
              component={RouterLink}
              to="/daily"
              aria-label="Play today's puzzles"
              title="Play: Scorigami Daily"
              sx={{ ...linkButtonSx, color: BRAND.accent, borderColor: accentTint(0.45), background: GAME_PAGES.includes(currentPage) ? accentTint(0.16) : accentTint(0.06) }}
            >
              <ExtensionRounded sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          <IconButton
            onClick={() => setSearching(true)}
            onPointerEnter={loadSearch}
            onFocus={loadSearch}
            aria-label="Search the site"
            title="Search (⌘K)"
            sx={linkButtonSx}
          >
            <SearchIcon sx={{ fontSize: 21 }} />
          </IconButton>
          {!tiny && (
            <IconButton href={X_URL} target="_blank" rel="noopener noreferrer" aria-label="Follow @footygami on X" sx={linkButtonSx}>
              <XIcon size={17} />
            </IconButton>
          )}
          {!isMobile && (
            <IconButton
              href="https://buymeacoffee.com/masonjmaier"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Buy me a coffee"
              sx={linkButtonSx}
            >
              <CoffeeIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
      </Toolbar>
      {searching && (
        <Suspense fallback={null}>
          <SearchDialog onClose={() => setSearching(false)} />
        </Suspense>
      )}
    </AppBar>
  );
}
